import React, { useCallback, useEffect, useState } from 'react';
import { creditsAPI } from '../services/api';
import CreditHistory from './CreditHistory';
import ConfirmDialog from './ConfirmDialog';

// initialEmail: look that customer up on mount (used by the Customers page modal)
function AdminCredits({ initialEmail = '' }) {
  const [email, setEmail] = useState(initialEmail);
  const [found, setFound] = useState(null);     // { user, balance }
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [pending, setPending] = useState(null);   // { delta, note } awaiting confirmation

  const foundUserId = found ? found.user.id : null;

  const lookupEmail = useCallback(async (address) => {
    setError(''); setMessage(''); setFound(null);
    setBusy(true);
    try {
      const res = await creditsAPI.adminLookup(address.trim());
      setFound({ user: res.data.user, balance: res.data.balance });
    } catch (err) {
      setError(err.response?.data?.error || 'Lookup failed');
    } finally {
      setBusy(false);
    }
  }, []);

  const lookup = () => lookupEmail(email);

  useEffect(() => {
    if (initialEmail) lookupEmail(initialEmail);
  }, [initialEmail, lookupEmail]);

  const adjust = async (sign) => {
    setError(''); setMessage('');
    const trimmed = amount.trim();
    if (!found || !/^\d+$/.test(trimmed)) { setError('Enter a whole number of credits.'); return; }
    const delta = sign * parseInt(trimmed, 10);
    if (!Number.isInteger(delta) || delta === 0) { setError('Enter a whole number of credits.'); return; }
    if (!note.trim()) { setError('A note is required.'); return; }
    setPending({ delta, note: note.trim() });
  };

  // Runs only after the operator confirms the dialog
  const applyAdjustment = async () => {
    if (!found || !pending) return;
    const { delta, note: pendingNote } = pending;
    setPending(null);
    setBusy(true);
    try {
      const res = await creditsAPI.adminAdjust({ user_id: found.user.id, delta, note: pendingNote });
      setFound({ ...found, balance: res.data.balance });
      // The server floors removals at 0, so report the amount actually removed
      const changed = delta > 0 ? delta : Math.max(0, found.balance.total - res.data.balance.total);
      setMessage(`${delta > 0 ? 'Added' : 'Removed'} ${changed} credits. New balance ${res.data.balance.total}.`);
      setAmount(''); setNote('');
      setHistoryKey((k) => k + 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Adjustment failed');
    } finally {
      setBusy(false);
    }
  };

  // Stable per found user; only rendered (and so only called) when found is set.
  const historyFetcher = useCallback(
    (params) => creditsAPI.adminHistory(foundUserId, params),
    [foundUserId]
  );

  return (
    <div className="admin-credits">
      <h2 className="admin-modal-title">Credits</h2>

      <div className="admin-credits-row">
        <input type="email" className="form-input" placeholder="User email" value={email}
               onChange={(e) => setEmail(e.target.value)} style={{ flex: '1 1 16rem', width: 'auto' }} />
        <button type="button" className="btn btn-primary" onClick={lookup} disabled={busy || !email.trim()}>Find user</button>
      </div>

      {error && <div className="admin-error" style={{ marginTop: 10 }}>{error}</div>}
      {message && <div className="admin-notice" style={{ marginTop: 10 }}>{message}</div>}

      {found && (
        <div style={{ marginTop: 14 }}>
          <dl className="admin-details">
            <div><dt>Customer</dt><dd>{found.user.email}{found.user.name ? ` (${found.user.name})` : ''}</dd></div>
            <div>
              <dt>Balance</dt>
              <dd>
                <strong>{found.balance.total.toLocaleString()}</strong> credits
                ({found.balance.monthly.toLocaleString()} monthly + {found.balance.topup.toLocaleString()} top-up)
              </dd>
            </div>
          </dl>
          <div className="admin-credits-row">
            <input type="number" min="1" step="1" placeholder="Credits" value={amount}
                   onChange={(e) => setAmount(e.target.value)} className="form-input" style={{ width: '7rem' }} />
            <input type="text" className="form-input" placeholder="Note (required, e.g. trial credits)" value={note}
                   onChange={(e) => setNote(e.target.value)} style={{ flex: '1 1 14rem', width: 'auto' }} />
            <button type="button" className="btn btn-success" disabled={busy} onClick={() => adjust(1)}>Add</button>
            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => adjust(-1)}>Remove</button>
          </div>
          <h3 className="admin-section-title">History</h3>
          <CreditHistory key={`${found.user.id}-${historyKey}`} fetcher={historyFetcher} limit={20} />
        </div>
      )}

      <ConfirmDialog
        isOpen={!!pending}
        onClose={() => setPending(null)}
        onConfirm={applyAdjustment}
        title="Confirm credit adjustment"
        message={pending && found
          ? `${pending.delta > 0 ? 'Add' : 'Remove'} ${Math.abs(pending.delta).toLocaleString()} credits ${pending.delta > 0 ? 'to' : 'from'} ${found.user.email} (${pending.delta > 0 ? '+' : '-'}${Math.abs(pending.delta).toLocaleString()})? Note: ${pending.note}`
          : ''}
        confirmText={pending && pending.delta > 0 ? 'Add credits' : 'Remove credits'}
        variant={pending && pending.delta > 0 ? 'primary' : 'danger'}
      />
    </div>
  );
}

export default AdminCredits;
